/**
 * Section B4 Page
 * B.4.基準線情境
 * 兩頁籤多富文本模式：頁籤1(4欄位) + 頁籤2(2欄位)，各頁籤個別儲存/重置
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import sectionB4Service, { SectionB4Detail, SectionB4Update } from '../services/sectionB4Service';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import '../styles/DataTable.css';

const SectionB4Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProjectId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  const [activeTab, setActiveTab] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 表單狀態
  const [formData, setFormData] = useState({
    baseline_scenario: '',
    baseline_equipment: '',
    baseline_procedure: '',
    future_demand: '',
    bau_scenario: '',
    bau_procedure: ''
  });
  const [originalData, setOriginalData] = useState({ ...formData });

  const tabs = [
    { key: 1, label: i18n.language === 'zh-TW' ? 'B.4.1.基準情境之辨識' : 'B.4.1. Identification of the baseline scenario' },
    { key: 2, label: i18n.language === 'zh-TW' ? 'B.4.2.BAU 情境或參考基準之辨識' : 'B.4.2. Identification of the BAU scenario or reference benchmark' }
  ];

  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('SectionB4Page');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  useEffect(() => {
    if (!selectedProjectId) {
      const empty = { baseline_scenario: '', baseline_equipment: '', baseline_procedure: '', future_demand: '', bau_scenario: '', bau_procedure: '' };
      setFormData(empty);
      setOriginalData(empty);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await sectionB4Service.getSectionB4ByProject(selectedProjectId);
        if (data) {
          const values = {
            baseline_scenario: data.baseline_scenario || '',
            baseline_equipment: data.baseline_equipment || '',
            baseline_procedure: data.baseline_procedure || '',
            future_demand: data.future_demand || '',
            bau_scenario: data.bau_scenario || '',
            bau_procedure: data.bau_procedure || ''
          };
          setFormData(values);
          setOriginalData(values);
        } else {
          const empty = { baseline_scenario: '', baseline_equipment: '', baseline_procedure: '', future_demand: '', bau_scenario: '', bau_procedure: '' };
          setFormData(empty);
          setOriginalData(empty);
        }
      } catch (err: any) {
        if (err.response?.status === 404 || err.response?.status === 500) {
          const empty = { baseline_scenario: '', baseline_equipment: '', baseline_procedure: '', future_demand: '', bau_scenario: '', bau_procedure: '' };
          setFormData(empty);
          setOriginalData(empty);
        } else {
          setError(err.response?.data?.detail || (i18n.language === 'zh-TW' ? '載入資料失敗' : 'Failed to load data'));
        }
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [selectedProjectId, i18n.language]);

  // 各頁籤個別儲存
  const handleSave = async () => {
    if (!selectedProjectId) return;
    try {
      setLoading(true);
      setError(null);

      const updateData: SectionB4Update = {};
      if (activeTab === 1) {
        updateData.baseline_scenario = formData.baseline_scenario || undefined;
        updateData.baseline_equipment = formData.baseline_equipment || undefined;
        updateData.baseline_procedure = formData.baseline_procedure || undefined;
        updateData.future_demand = formData.future_demand || undefined;
      } else {
        updateData.bau_scenario = formData.bau_scenario || undefined;
        updateData.bau_procedure = formData.bau_procedure || undefined;
      }

      const updated = await sectionB4Service.updateSectionB4(selectedProjectId, updateData);
      const values = {
        baseline_scenario: updated.baseline_scenario || '',
        baseline_equipment: updated.baseline_equipment || '',
        baseline_procedure: updated.baseline_procedure || '',
        future_demand: updated.future_demand || '',
        bau_scenario: updated.bau_scenario || '',
        bau_procedure: updated.bau_procedure || ''
      };
      if (activeTab === 1) {
        setOriginalData(prev => ({ ...prev, baseline_scenario: values.baseline_scenario, baseline_equipment: values.baseline_equipment, baseline_procedure: values.baseline_procedure, future_demand: values.future_demand }));
      } else {
        setOriginalData(prev => ({ ...prev, bau_scenario: values.bau_scenario, bau_procedure: values.bau_procedure }));
      }
      toast.success(i18n.language === 'zh-TW' ? '儲存成功' : 'Saved successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.detail || (i18n.language === 'zh-TW' ? '儲存失敗' : 'Save failed'));
    } finally {
      setLoading(false);
    }
  };

  // 各頁籤個別重置
  const handleReset = () => {
    if (activeTab === 1) {
      setFormData(prev => ({ ...prev, baseline_scenario: originalData.baseline_scenario, baseline_equipment: originalData.baseline_equipment, baseline_procedure: originalData.baseline_procedure, future_demand: originalData.future_demand }));
    } else {
      setFormData(prev => ({ ...prev, bau_scenario: originalData.bau_scenario, bau_procedure: originalData.bau_procedure }));
    }
    setError(null);
  };

  if (permissionLoading) return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  if (!hasPermission('section_b4', 'read')) return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;

  const canEdit = hasPermission('section_b4', 'update');

  // 頁籤1 欄位定義
  const tab1Fields = [
    { key: 'baseline_scenario', label: i18n.language === 'zh-TW' ? '基準情境描述' : 'Baseline scenario description', placeholder: i18n.language === 'zh-TW' ? '請輸入基準情境描述...' : 'Please enter baseline scenario description...' },
    { key: 'baseline_equipment', label: i18n.language === 'zh-TW' ? '設施、系統與設備資訊' : 'Information on facilities, systems and equipment', placeholder: i18n.language === 'zh-TW' ? '請輸入設施、系統與設備資訊...' : 'Please enter information on facilities, systems and equipment...' },
    { key: 'baseline_procedure', label: i18n.language === 'zh-TW' ? '逐步程序說明' : 'Step-by-step procedure', placeholder: i18n.language === 'zh-TW' ? '請輸入逐步程序說明...' : 'Please enter step-by-step procedure...' },
    { key: 'future_demand', label: i18n.language === 'zh-TW' ? '未來排放與抑制需求' : 'Future emissions & suppressed demand', placeholder: i18n.language === 'zh-TW' ? '請輸入未來排放與抑制需求...' : 'Please enter future emissions & suppressed demand...' }
  ];

  const tab2Fields = [
    { key: 'bau_scenario', label: i18n.language === 'zh-TW' ? 'BAU情境描述' : 'BAU Scenario or reference benchmark description', placeholder: i18n.language === 'zh-TW' ? '請輸入BAU情境描述...' : 'Please enter BAU scenario description...' },
    { key: 'bau_procedure', label: i18n.language === 'zh-TW' ? 'BAU逐步程序說明' : 'Step-by-step procedure', placeholder: i18n.language === 'zh-TW' ? '請輸入BAU逐步程序說明...' : 'Please enter BAU step-by-step procedure...' }
  ];

  const currentFields = activeTab === 1 ? tab1Fields : tab2Fields;

  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="section_b4" />
      </div>

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

      {selectedProjectId && (
        <div>
          {/* 頁籤 */}
          <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: '16px' }}>
            {tabs.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
                padding: '10px 20px', border: 'none',
                borderBottom: activeTab === tab.key ? '3px solid #4A90E2' : '3px solid transparent',
                background: activeTab === tab.key ? '#f0f7ff' : 'transparent',
                color: activeTab === tab.key ? '#4A90E2' : '#666',
                fontWeight: activeTab === tab.key ? 600 : 400,
                cursor: 'pointer', fontSize: '14px', marginBottom: '-2px'
              }}>
                {tab.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="loading">{t('common.loading')}</div>
          ) : (
            <>
              {currentFields.map(field => (
                <div key={field.key} style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>{field.label}</label>
                  <RichTextEditor
                    value={(formData as any)[field.key]}
                    onChange={(html) => setFormData(prev => ({ ...prev, [field.key]: html }))}
                    placeholder={field.placeholder}
                    disabled={!canEdit}
                  />
                </div>
              ))}

              {canEdit && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '24px' }}>
                  <button className="btn-primary" onClick={handleSave} disabled={loading}>
                    {loading ? t('message.saving') : t('common.save')}
                  </button>
                  <button className="btn-secondary" onClick={handleReset} disabled={loading}>
                    {t('common.reset')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!selectedProjectId && !loading && (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
          {i18n.language === 'zh-TW' ? '請先在上方選擇專案' : 'Please select a project from the top bar'}
        </div>
      )}
    </div>
  );
};

export default SectionB4Page;
